import { useCallback, useMemo, useState } from "react";
import { getDisplayLabel } from "../../utils/summary/summaryPageHelpers";
import type { ForecastMetaDataType } from "../useFetchForecastMetaData";
import { GRAPH_ALLOWED_METRICS } from "./summaryGridHelpers";

/** Parameters for the summary graph state hook. */
interface UseSummaryGraphStateParams {
  /** Metrics sorted by KPI sort order. */
  sortedMetrics: string[];
  /** Labels for each table in the grid. */
  tableLabels: string[];
  /** Forecast metadata (used for display labels). */
  metaData: ForecastMetaDataType | undefined;
}

/** Manages graph visibility, metric selection, and table selection. */
export function useSummaryGraphState({
  sortedMetrics,
  tableLabels,
  metaData
}: UseSummaryGraphStateParams) {
  const [graphVisible, setGraphVisible] = useState(false);
  const [graphMetric, setGraphMetric] = useState("GS1");
  const [graphTableIndex, setGraphTableIndex] = useState(0);

  // Called on Apply: the new response may have different tables, so an
  // index into the previous response's tables must not carry over. The
  // metric is a view preference (metric codes are stable across
  // contexts), so it deliberately survives.
  const resetGraphSelection = useCallback(function resetGraphSelection() {
    setGraphTableIndex(0);
  }, []);

  const graphMetricOptions = useMemo(() => {
    const available = sortedMetrics.filter((m) => GRAPH_ALLOWED_METRICS.has(m));
    return available.map((m) => ({
      label: getDisplayLabel(m, metaData),
      value: m
    }));
  }, [sortedMetrics, metaData]);

  const graphTableOptions = useMemo(
    () => tableLabels.map((label, i) => ({ label, value: String(i) })),
    [tableLabels]
  );

  return {
    graphVisible,
    setGraphVisible,
    graphMetric,
    setGraphMetric,
    graphTableIndex,
    setGraphTableIndex,
    graphMetricOptions,
    graphTableOptions,
    resetGraphSelection
  };
}
